import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ShareIcon,
  EyeIcon,
  CalendarIcon,
  ClockIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowLeftIcon,
  LinkIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

function ShareManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shareLinks, setShareLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Filter states
  const [domainFilter, setDomainFilter] = useState(searchParams.get('domain') || '');
  const [jobIdFilter, setJobIdFilter] = useState(searchParams.get('jobId') || '');
  const [availableDomains, setAvailableDomains] = useState([]);
  const [availableJobs, setAvailableJobs] = useState([]);

  useEffect(() => {
    fetchShareLinks();
  }, [domainFilter, jobIdFilter]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (domainFilter) params.set('domain', domainFilter);
    if (jobIdFilter) params.set('jobId', jobIdFilter);
    setSearchParams(params, { replace: true });
  }, [domainFilter, jobIdFilter, setSearchParams]);

  const fetchShareLinks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (domainFilter) params.append('domain', domainFilter);
      if (jobIdFilter) params.append('jobId', jobIdFilter);

      const response = await api.get(`/share/list/all?${params.toString()}`);

      if (response.success) {
        const links = response.data || [];
        setShareLinks(links);

        // Extract unique domains and jobs for filter options
        const domains = [...new Set(links.map(link => {
          try {
            const url = new URL(link.jobUrl || '');
            return url.hostname;
          } catch {
            return null;
          }
        }).filter(Boolean))];

        const jobs = [...new Set(links.map(link => ({
          jobId: link.job_id,
          url: link.jobUrl,
          title: `${link.jobUrl} (${formatDate(link.jobCreatedAt)})`
        })))];

        setAvailableDomains(domains);
        setAvailableJobs(jobs);
      } else {
        setError(response.error || 'Failed to load shared links');
      }
    } catch (err) {
      setError('Failed to load shared links');
      console.error('Error fetching share links:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async (shareUrl, id) => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleRevokeLink = async (linkId, title) => {
    const confirmMessage = `Revoke shared link for "${title}"?\n\nThis will make the link inaccessible to anyone who has it. This action cannot be undone.`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingId(linkId);
      const response = await api.delete(`/share/${linkId}`);
      
      if (response.success) {
        setShareLinks(prev => prev.filter(link => link.id !== linkId));
      } else {
        alert('Failed to revoke link: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error revoking link: ' + err.message);
      console.error('Error revoking link:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getExpirationText = (expiresAt) => {
    if (!expiresAt) return 'Never expires';
    
    const expiry = new Date(expiresAt);
    const now = new Date();
    
    if (expiry < now) {
      return 'Expired';
    }
    
    const diffHours = Math.round((expiry - now) / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      return `Expires in ${diffHours}h`;
    } else {
      const diffDays = Math.round(diffHours / 24);
      return `Expires in ${diffDays}d`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading shared links...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                    Back to Dashboard
                  </button>
                </div>
                <h1 className="text-2xl font-bold text-foreground mt-2">Share Management</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Manage your shared analysis links and track their usage
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ShareIcon className="h-5 w-5" />
                <span>{shareLinks.length} shared link{shareLinks.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="bg-card rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <FunnelIcon className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-sm font-medium text-foreground">Filter Shared Links</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Domain Filter */}
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-1">
                Filter by Domain
              </label>
              <div className="relative">
                <select
                  value={domainFilter}
                  onChange={(e) => setDomainFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All domains</option>
                  {availableDomains.map(domain => (
                    <option key={domain} value={domain}>{domain}</option>
                  ))}
                </select>
                {domainFilter && (
                  <button
                    onClick={() => setDomainFilter('')}
                    className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-muted-foreground"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Job Filter */}
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-1">
                Filter by Job
              </label>
              <div className="relative">
                <select
                  value={jobIdFilter}
                  onChange={(e) => setJobIdFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All jobs</option>
                  {availableJobs.map(job => (
                    <option key={job.jobId} value={job.jobId}>{job.title}</option>
                  ))}
                </select>
                {jobIdFilter && (
                  <button
                    onClick={() => setJobIdFilter('')}
                    className="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-muted-foreground"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {(domainFilter || jobIdFilter) && (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {domainFilter && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Domain: {domainFilter}
                  <button
                    onClick={() => setDomainFilter('')}
                    className="ml-1.5 text-blue-600 hover:text-blue-800"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              {jobIdFilter && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Job: {availableJobs.find(j => j.jobId === jobIdFilter)?.url || jobIdFilter}
                  <button
                    onClick={() => setJobIdFilter('')}
                    className="ml-1.5 text-green-600 hover:text-green-800"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              )}
              <button
                onClick={() => {
                  setDomainFilter('');
                  setJobIdFilter('');
                }}
                className="text-sm text-muted-foreground hover:text-card-foreground underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {shareLinks.length === 0 ? (
          <div className="bg-card rounded-lg shadow p-8 text-center">
            <LinkIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Shared Links</h3>
            <p className="text-muted-foreground mb-6">
              You haven't shared any analysis dashboards yet. Create shareable links from your analysis results.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {shareLinks.map((link) => {
              const expired = isExpired(link.expires_at);
              const isActive = link.is_active && !expired;
              
              return (
                <div
                  key={link.id}
                  className={`bg-card rounded-lg shadow border-l-4 ${
                    isActive ? 'border-l-green-500' : 'border-l-gray-400'
                  } p-6`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">{link.title}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {isActive ? 'Active' : expired ? 'Expired' : 'Inactive'}
                        </span>
                      </div>
                      
                      {link.description && (
                        <p className="text-muted-foreground mb-3">{link.description}</p>
                      )}

                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          <span>Created {formatDate(link.created_at)}</span>
                        </div>

                        <div className="flex items-center">
                          <EyeIcon className="h-4 w-4 mr-1" />
                          <span>{link.view_count || 0} view{(link.view_count || 0) !== 1 ? 's' : ''}</span>
                        </div>

                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          <span>{getExpirationText(link.expires_at)}</span>
                        </div>
                      </div>

                      {/* Job URL */}
                      {link.jobUrl && (
                        <div className="mb-3 p-2 bg-background rounded">
                          <span className="text-sm font-medium text-card-foreground">Analysis URL: </span>
                          <span className="text-sm text-muted-foreground">{link.jobUrl}</span>
                        </div>
                      )}

                      {/* Shared Content */}
                      <div className="mb-4">
                        <span className="text-sm font-medium text-card-foreground">Shared content: </span>
                        <span className="text-sm text-muted-foreground">
                          {(link.shared_content || []).map(content => {
                            const contentLabels = {
                              actions: 'SEO Actions',
                              executive_summary: 'Executive Summary',
                              job_metadata: 'Analysis Details',
                              progress_tracking: 'Progress Tracking'
                            };
                            return contentLabels[content] || content;
                          }).join(', ') || 'Default content'}
                        </span>
                      </div>

                      {/* Share URL */}
                      <div className="flex items-center gap-2 p-3 bg-background rounded-md">
                        <input
                          type="text"
                          value={link.shareUrl}
                          readOnly
                          className="flex-1 bg-transparent text-sm text-muted-foreground border-none focus:outline-none"
                        />
                        <button
                          onClick={() => handleCopyLink(link.shareUrl, link.id)}
                          className="p-1 text-muted-foreground hover:text-card-foreground transition-colors"
                          title="Copy link"
                        >
                          {copiedId === link.id ? (
                            <CheckIcon className="h-5 w-5 text-green-600" />
                          ) : (
                            <ClipboardDocumentIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 ml-6">
                      {isActive && (
                        <a
                          href={link.shareUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          View
                        </a>
                      )}
                      
                      <button
                        onClick={() => handleRevokeLink(link.id, link.title)}
                        disabled={deletingId === link.id}
                        className="inline-flex items-center px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        {deletingId === link.id ? (
                          <>
                            <div className="animate-spin h-4 w-4 mr-1 border border-white border-t-transparent rounded-full"></div>
                            Revoking...
                          </>
                        ) : (
                          <>
                            <TrashIcon className="h-4 w-4 mr-1" />
                            Revoke
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ShareManagement;